import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Search,
  Plus,
  User,
  Edit,
  Trash2,
  Eye,
  Play,
  StopCircle,
  Loader2,
  Clock,
  MoreVertical,
  Settings,
  Key,
  Monitor,
  Clock as ClockIcon,
} from "lucide-react";
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getEmployeeById,
  seedEmployees,
  updateEmployeeStartStop,
  getActivitySetting,
  updateActivitySetting,
} from "../api/moniter";
import { getManagerList, getDepartmentList } from "../redux/slices/employee";
import { User as UserType } from "../types";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";
import { dispatch, useSelector } from "../redux/store";

export function ScreenRecording() {
  const [employees, setEmployees] = useState<UserType[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState<UserType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showActivitySettingModal, setShowActivitySettingModal] = useState(false);
  const [activitySetting, setActivitySetting] = useState({ activityMinutes: 15 });
  const [activitySettingLoading, setActivitySettingLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState<{
    [key: string]: { startStop?: boolean; overtime?: boolean; delete?: boolean };
  }>({});
  const { toast } = useToast();
  const { user } = useAuth();

  const canManageEmployees = user?.role === "admin" || user?.role === "hr";

  useEffect(() => {
    fetchEmployees();
    fetchActivitySetting();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchQuery, departmentFilter, roleFilter]);

  const fetchEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.monitors || []);
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to load employees", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchActivitySetting = async () => {
    try {
      const response = await getActivitySetting();
      if (response?.data?.activityMinutes) {
        setActivitySetting({ activityMinutes: response.data.activityMinutes });
      }
    } catch (error) {
      console.error("Error fetching activity setting:", error);
    }
  };

  const handleSaveActivitySetting = async () => {
    try {
      setActivitySettingLoading(true);
      await updateActivitySetting(activitySetting);
      toast({ title: "Success", description: "Activity setting updated successfully" });
      setShowActivitySettingModal(false);
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to update activity setting", variant: "destructive" });
    } finally {
      setActivitySettingLoading(false);
    }
  };

  const filterEmployees = () => {
    let filtered = employees;
    if (searchQuery) {
      filtered = filtered.filter(
        (emp) =>
          emp.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.employeeID?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.loginID?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    if (departmentFilter !== "all") filtered = filtered.filter((emp) => emp.department === departmentFilter);
    if (roleFilter !== "all") filtered = filtered.filter((emp) => emp.role === roleFilter);
    setFilteredEmployees(filtered);
  };

  const handleAddEmployee = async (employeeData: Partial<UserType>) => {
    try {
      await createEmployee(employeeData);
      toast({ title: "Success", description: "Employee added successfully" });
      setShowAddModal(false);
      fetchEmployees();
    } catch (error) {
      toast({ title: "Error", description: "Employee already exist" });
    }
  };

  const handleEditEmployee = async (employeeData: Partial<UserType>) => {
    if (!selectedEmployee) return;
    try {
      await updateEmployee(selectedEmployee.id, employeeData);
      toast({ title: "Success", description: "Employee updated successfully" });
      setShowEditModal(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to update employee", variant: "destructive" });
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], delete: true } }));
      await deleteEmployee(id);
      toast({ title: "Success", description: "Employee deleted successfully" });
      fetchEmployees();
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to delete employee", variant: "destructive" });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], delete: false } }));
    }
  };

  const handleViewEmployee = async (id: string) => {
    try {
      const employeeFromList = employees.find((emp) => emp.id === id);
      if (employeeFromList) {
        setSelectedEmployee(employeeFromList);
      } else {
        const response = await getEmployeeById(id);
        setSelectedEmployee(response.employee);
      }
      setShowViewModal(true);
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to load employee details", variant: "destructive" });
    }
  };

  const handleStartStopEmployee = async (id: string, isStart: boolean, isOvertime: boolean) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], startStop: true } }));
      await updateEmployeeStartStop(id, !isStart, isOvertime);
      toast({ title: "Success", description: !isStart ? "Screen recording started" : "Screen recording stopped" });
      fetchEmployees();
    } catch (error) {
      toast({ description: error.message, variant: "destructive" });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], startStop: false } }));
    }
  };

  const handleOvertimeClick = async (employeeId: string, isOvertime: boolean) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [employeeId]: { ...prev[employeeId], is_overtime: true } }));
      await updateEmployeeStartStop(employeeId, true, !isOvertime);
      toast({ title: "Success", description: "Overtime started for employee" });
      fetchEmployees();
    } catch (error) {
      toast({ description: error.message || "Failed to start overtime", variant: "destructive" });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [employeeId]: { ...prev[employeeId], is_overtime: false } }));
    }
  };

  const departments = [...new Set(employees.map((emp) => emp.department))];
  const roles = [...new Set(employees.map((emp) => emp.role))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3 sm:p-4 md:p-6">
      {/* Header - mobile responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
            Screen Recording Summary
          </h1>
          <p className="text-gray-600 text-xs sm:text-sm mt-1">
            Manage your organization's workforce
          </p>
        </div>
        {canManageEmployees && (
          <div className="flex flex-row gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setShowActivitySettingModal(true)}
              className="text-gray-500 hover:text-gray-700 border-gray-300 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
            >
              <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="whitespace-nowrap">Activity Configure</span>
            </Button>
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3">
                  <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">Create Schedule</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[95vw] max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>New Schedule</DialogTitle>
                  <DialogDescription>
                    Fill in the details to track your employee work
                  </DialogDescription>
                </DialogHeader>
                <EmployeeForm onSubmit={handleAddEmployee} />
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Activity Setting Modal */}
      <Dialog open={showActivitySettingModal} onOpenChange={setShowActivitySettingModal}>
        <DialogContent className="w-[95vw] max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Activity Settings</DialogTitle>
            <DialogDescription>
              Configure activity tracking settings for employees
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="activityMinutes">Activity Interval</Label>
              <Select
                value={String(activitySetting.activityMinutes)}
                onValueChange={(value) =>
                  setActivitySetting({ ...activitySetting, activityMinutes: parseInt(value) })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select activity interval" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="20">20 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Time gap between each data capture from Work Monitor
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowActivitySettingModal(false)} className="text-sm">
              Cancel
            </Button>
            <Button
              onClick={handleSaveActivitySetting}
              disabled={activitySettingLoading}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-sm"
            >
              {activitySettingLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters - fully stacked on mobile */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardContent className="p-3 sm:p-4 md:p-6">
          <div className="flex flex-col gap-3">
            {/* Search full width */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
            {/* Filters - side by side on mobile, row on desktop */}
            <div className="grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-3">
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-full md:w-48 text-xs sm:text-sm">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-48 text-xs sm:text-sm">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Grid - 1 col mobile, 2 col tablet, 3 col desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => (
          <Card
            key={employee.id}
            className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
          >
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center justify-between">
                <Avatar className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
                  <AvatarImage src={employee.profilePhoto} alt={employee.employeeName} />
                  <AvatarFallback className="text-sm">
                    {employee.employeeName?.charAt(0) || "E"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant={employee.start ? "default" : "secondary"}
                    className={`${employee.start ? "bg-green-500" : "bg-gray-500"} text-xs px-2 py-0.5`}
                  >
                    {employee.start ? "Active" : "Inactive"}
                  </Badge>
                  {canManageEmployees && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white min-w-[180px]">
                        <DropdownMenuItem
                          onClick={() => handleViewEmployee(employee.id)}
                          className="cursor-pointer text-sm"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setSelectedEmployee(employee); setShowEditModal(true); }}
                          className="cursor-pointer text-sm"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Employee
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleStartStopEmployee(employee.id, employee.start, employee.is_overtime)}
                          disabled={loadingStates[employee.id]?.startStop}
                          className={`cursor-pointer text-sm ${employee.start ? "text-red-600" : "text-green-600"}`}
                        >
                          {loadingStates[employee.id]?.startStop ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{employee.start ? "Stopping..." : "Starting..."}</>
                          ) : employee.start ? (
                            <><StopCircle className="h-4 w-4 mr-2" />Stop Recording</>
                          ) : (
                            <><Play className="h-4 w-4 mr-2" />Start Recording</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleOvertimeClick(employee.id, employee.is_overtime)}
                          disabled={loadingStates[employee.id]?.is_overtime}
                          className="cursor-pointer text-sm text-orange-600"
                        >
                          {loadingStates[employee.id]?.is_overtime ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting Overtime...</>
                          ) : (
                            <><Clock className="h-4 w-4 mr-2" />{employee.is_overtime ? "Stop Overtime" : "Start Overtime"}</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteEmployee(employee.id)}
                          disabled={loadingStates[employee.id]?.delete}
                          className="cursor-pointer text-sm text-red-600"
                        >
                          {loadingStates[employee.id]?.delete ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                          ) : (
                            <><Trash2 className="h-4 w-4 mr-2" />Delete Employee</>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              <div className="mt-2">
                <CardTitle className="text-base sm:text-lg leading-tight">
                  {employee.employeeName}
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm mt-0.5">
                  {employee.shift || "No shift assigned"}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 px-4 pb-4">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{employee.employeeName}</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Key className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{employee.employeeID}</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <ClockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate text-xs">
                  {employee.shift === "Anytime" ? "Shift 24hr" : `${employee.startTime} - ${employee.endTime}`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Monitor className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="text-xs">
                  {employee.datatype === "screenshot" ? "Screenshot" : "Screen Recording"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Key className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="text-xs truncate">ID: {employee.loginID}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View Employee Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="w-[95vw] max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Work Schedule Details</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Complete information about {selectedEmployee?.employeeName}
            </DialogDescription>
          </DialogHeader>
          {selectedEmployee && <EmployeeDetails employee={selectedEmployee} />}
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="w-[95vw] max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee information.</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <EmployeeForm employee={selectedEmployee} onSubmit={handleEditEmployee} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmployeeForm({
  employee,
  onSubmit,
}: {
  employee?: UserType;
  onSubmit: (data: Partial<UserType>) => void;
}) {
  const [formData, setFormData] = useState({
    startTime: employee?.startTime || "",
    endTime: employee?.endTime || "",
    shift: employee?.shift || "Day",
    interval: employee?.interval || 15,
    keyboardInterval: employee?.keyboardInterval || 15,
    department_id: employee?.department_id || "",
    emptID: employee?.emptID || "",
    employeeID: employee?.employeeID || "",
    start: false,
    stop: employee?.stop || false,
    weeks: employee?.weeks || [],
    sunday: employee?.weeks?.[0]?.sunday === undefined ? false : employee?.weeks?.[0]?.sunday,
    monday: employee?.weeks?.[0]?.monday === undefined ? true : employee?.weeks?.[0]?.monday,
    tuesday: employee?.weeks?.[0]?.tuesday === undefined ? true : employee?.weeks?.[0]?.tuesday,
    wednesday: employee?.weeks?.[0]?.wednesday === undefined ? true : employee?.weeks?.[0]?.wednesday,
    thursday: employee?.weeks?.[0]?.thursday === undefined ? true : employee?.weeks?.[0]?.thursday,
    friday: employee?.weeks?.[0]?.friday === undefined ? true : employee?.weeks?.[0]?.friday,
    saturday: employee?.weeks?.[0]?.saturday === undefined ? true : employee?.weeks?.[0]?.saturday,
    datatype: employee?.datatype || "screenshot",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  useEffect(() => {
    if (formData.role !== "manager" && formData.department_id) {
      dispatch(getManagerList(formData.department_id));
    }
  }, [formData.role, formData.department_id]);

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  const departmentList = useSelector((state) => state.employee.departmentList);
  const managerList = useSelector((state) => state.employee.managerList);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Grid: 1 col on mobile, 2 on sm, 4 on lg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="startTime" className="text-sm">Start Time</Label>
          <Input
            type="time"
            id="startTime"
            value={formData.startTime}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="endTime" className="text-sm">End Time</Label>
          <Input
            type="time"
            id="endTime"
            value={formData.endTime}
            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-sm">Work Shift</Label>
          <Select
            value={formData.shift}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, shift: value }))}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Work Shift" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="Day">Day</SelectItem>
              <SelectItem value="Night">Night</SelectItem>
              <SelectItem value="Anytime">24hr</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Track Type</Label>
          <Select
            value={formData.datatype}
            onValueChange={(value) => setFormData({ ...formData, datatype: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Track Type" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="screenrecording">Screen Recording</SelectItem>
              <SelectItem value="screenshot">Screen Shot</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.datatype === "screenshot" && (
          <div>
            <Label className="text-sm">Interval</Label>
            <Select
              value={Number(formData.interval)}
              onValueChange={(value) => setFormData({ ...formData, interval: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select Interval" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value={5}>5 mins</SelectItem>
                <SelectItem value={10}>10 mins</SelectItem>
                <SelectItem value={15}>15 mins</SelectItem>
                <SelectItem value={30}>30 mins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-sm">Activity Interval</Label>
          <Select
            value={Number(formData.keyboardInterval)}
            onValueChange={(value) => setFormData({ ...formData, keyboardInterval: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Interval" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value={3}>3 mins</SelectItem>
              <SelectItem value={5}>5 mins</SelectItem>
              <SelectItem value={10}>10 mins</SelectItem>
              <SelectItem value={15}>15 mins</SelectItem>
              <SelectItem value={30}>30 mins</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="employeeID" className="text-sm">Employee ID</Label>
          <Input
            id="employeeID"
            value={formData.employeeID}
            disabled={formData.emptID}
            onChange={(e) => setFormData({ ...formData, employeeID: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-sm">Department</Label>
          <Select
            value={formData.department_id}
            disabled={formData.employeeID}
            onValueChange={(value) => setFormData({ ...formData, department_id: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {departmentList.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Select Manager</Label>
          <Select
            value={formData.emptID}
            disabled={formData.employeeID}
            onValueChange={(value) => setFormData({ ...formData, emptID: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Manager" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {managerList.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Working Days */}
      <div>
        <h2 className="text-sm font-semibold mb-3">Working Days</h2>
        <div className="flex flex-wrap gap-3 sm:gap-4">
          {[
            { key: "monday", label: "Mon" },
            { key: "tuesday", label: "Tue" },
            { key: "wednesday", label: "Wed" },
            { key: "thursday", label: "Thu" },
            { key: "friday", label: "Fri" },
            { key: "saturday", label: "Sat" },
            { key: "sunday", label: "Sun" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center space-x-1.5">
              <Checkbox
                id={key}
                checked={formData[key]}
                onCheckedChange={(checked) => setFormData({ ...formData, [key]: !!checked })}
              />
              <Label htmlFor={key} className="text-xs sm:text-sm font-medium cursor-pointer">
                {label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-sm"
        >
          {employee ? "Update" : "Start"} Schedule
        </Button>
      </div>
    </form>
  );
}

function EmployeeDetails({ employee }: { employee: UserType }) {
  return (
    <div className="space-y-4 text-sm">
      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Personal Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: "Employee Name", value: employee.employeeName },
            { label: "Employee ID", value: employee.employeeID, mono: true },
            { label: "Login ID", value: employee.loginID, mono: true },
            { label: "Password", value: employee.password, mono: true },
          ].map(({ label, value, mono }) => (
            <div key={label}>
              <Label className="text-gray-500 text-xs">{label}</Label>
              <div className={`text-gray-800 mt-1 text-xs sm:text-sm break-all ${mono ? "font-mono" : "font-medium"}`}>
                {value || "N/A"}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Schedule & Tracking</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Shift", value: employee.shift },
            { label: "Tracking Type", value: employee.datatype === "screenshot" ? "Screenshot" : "Screen Recording" },
            { label: "Start Time", value: employee.startTime },
            { label: "End Time", value: employee.endTime },
            ...(employee.datatype === "screenshot"
              ? [{ label: "Screenshot Interval", value: employee.interval ? `${employee.interval} min` : null }]
              : []),
            { label: "Activity Interval", value: employee.keyboardInterval ? `${employee.keyboardInterval} min` : null },
          ].map(({ label, value }) => (
            <div key={label}>
              <Label className="text-gray-500 text-xs">{label}</Label>
              <div className="text-gray-800 text-xs sm:text-sm mt-1">{value || "N/A"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Status</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-gray-500 text-xs">Recording Status</Label>
            <div className="mt-1">
              <Badge className={`${employee.start ? "bg-green-500" : "bg-gray-500"} text-xs`}>
                {employee.start ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
          <div>
            <Label className="text-gray-500 text-xs">Connection</Label>
            <div className="mt-1">
              <Badge className={`${employee.connected ? "bg-green-500" : "bg-red-500"} text-xs`}>
                {employee.connected ? "Connected" : "Disconnected"}
              </Badge>
            </div>
          </div>
          <div>
            <Label className="text-gray-500 text-xs">Overtime</Label>
            <div className="mt-1">
              <Badge className={`${employee.is_overtime ? "bg-orange-500" : "bg-gray-500"} text-xs`}>
                {employee.is_overtime ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {employee.weeks?.[0] && (
        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
          <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Working Days</h3>
          <div className="flex flex-wrap gap-2">
            {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map(
              (day) =>
                employee.weeks[0][day] && (
                  <Badge key={day} className="bg-blue-500 text-xs capitalize">{day}</Badge>
                )
            )}
          </div>
        </div>
      )}
    </div>
  );
}